import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageHeader from '../../components/ui/PageHeader'
import Button from '../../components/ui/Button'
import { useToast } from '../../components/ui/ToastProvider'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import ActionsListPanel from './components/ActionsListPanel'
import StandaloneActionFormDialog from './components/StandaloneActionFormDialog'
import { useCreateStandaloneAction } from './hooks/useActions'
import { type ActionScope, type StandaloneActionCreateRequest } from './types'
import '../admin/admin.css'
import '../meetings/meetings.css'
import './actions.css'

interface Props {
  /** Bu ekranın aksiyon kapsamı — rota ile sabittir (sol menü "Aksiyonlar" başlığı altındaki alt öğe). */
  scope: ActionScope
}

/**
 * Aksiyonlar alt sayfası (tek kapsam) — Tüm Aksiyonlar / Oluşturduklarım / Bana Atananlar / Takip Ettiklerim'in
 * HER BİRİ ayrı bir rotadır (sol menüde "Aksiyonlar" başlığı altındaki alt öğeler; eski sekmeli tek ekran
 * 2026-08-11'de kaldırıldı). Bu bileşen tek kapsamın listesini ({@link ActionsListPanel}) gösterir. Kapsam
 * görüntüleme yetkisi rota düzeyinde ({@code RequireGrant}) doğrulanır; başlık = menü adı (anayasa; kapsamla
 * aynı i18n anahtarı `actions.tabs.<scope>`). Sağ üstteki "Aksiyon Oluştur" yalnız {@code ACTION_CREATE}
 * yetkisinde görünür (asıl koruma backend'de).
 */
export default function ActionsPage({ scope }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const { hasGrant } = useGrants()
  const [formOpen, setFormOpen] = useState(false)
  const create = useCreateStandaloneAction()

  const onSubmit = (payload: StandaloneActionCreateRequest, files: File[]) => {
    create.mutate(
      { body: payload, files },
      {
        // Görev her hâlükârda oluştu; ek adımı patladıysa başarı DEĞİL uyarı gösterilir — kullanıcı
        // dosyanın eklenmediğini bilmeli ve Görev Detayı → Dokümanlar'dan tekrar denemeli.
        onSuccess: ({ filesFailed }) => {
          if (filesFailed) {
            toast.warn(t('actions.create.success'), t('actions.create.filesFailed'))
          } else {
            toast.success(t('actions.create.success'))
          }
          setFormOpen(false)
        },
        // Hata → merkezî toast (019).
      },
    )
  }

  return (
    <div className="page meetings-page">
      <PageHeader
        title={t(`actions.tabs.${scope}`)}
        actions={
          hasGrant(GRANTS.ACTION_CREATE) ? (
            <Button
              icon="pi pi-plus"
              label={t('actions.create.new')}
              onClick={() => setFormOpen(true)}
            />
          ) : undefined
        }
      />

      {/* key={scope}: dört alt sayfa AYNI bileşen ağacı konumunda olduğundan React, rota değişince paneli
          yeniden kullanır (remount etmez) → önceki kapsamın filtre/arama durumu sızardı ve kapsam-başına
          sessionStorage'a yanlış kapsamla yazılırdı. Kapsamı key yaparak her alt sayfa taze mount olur ve
          kendi (kapsam-başına) görünümünü yükler → filtreler dört menü için bağımsız çalışır. */}
      <ActionsListPanel key={scope} scope={scope} />

      <StandaloneActionFormDialog
        visible={formOpen}
        submitting={create.isPending}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />
    </div>
  )
}
